import Store from 'store';
import Data from 'data';
import { cubeGetStat, cubeGetStats, cubeSlotValid } from 'logic/cube';
import { paragonGetStats } from 'logic/paragon';
import { getSkillBonus, getPassiveBonus, getFollowerBonus } from 'logic/skills';
import { Seasons } from 'common';

const level = 70;

function addStat(dst, stat, amount, factor, source) {
  if (!amount) {
    return;
  }
  if (amount.source) {
    return addStat(dst, stat, amount.value, factor, amount.source);
  }
  const info = Data.statInfo[stat];
  if (!factor) {
    factor = 1;
  }
  if (dst.factors && dst.factors[stat]) {
    factor *= dst.factors[stat];
  }
  if (info && info.special && dst.special && source) {
    if (!dst.special[stat]) {
      dst.special[stat] = {};
    }
    if (!dst.special[stat][source]) {
      dst.special[stat][source] = [];
    }
    if (amount instanceof Array) {
      dst.special[stat][source].push({percent: amount[0] * factor});
      amount = amount[0];
    } else if (typeof amount === "number") {
      dst.special[stat][source].push({percent: amount * factor});
    } else {
      if (amount.list) {
        for (const value of amount.list) {
          addStat(dst, stat, value, factor, source);
        }
        return;
      }
      const obj = Object.assign({}, amount);
      obj.percent *= factor;
      dst.special[stat][source].push(obj);
      if (amount.elems == null && amount.pet == null && amount.skills == null) {
        amount = amount.percent;
      } else {
        amount = undefined;
      }
    }
    if (amount) {
      if (info.mult) {
        dst[stat] = (dst[stat] || 1) * (1 + 0.01 * amount * factor);
      } else {
        dst[stat] = (dst[stat] || 0) + amount * factor;
      }
    }
    return;
  }
  if (source && dst.sources) {
    const value = (typeof amount === "number" ? amount : amount[0]) * factor;
    if (value) {
      if (!dst.sources[stat]) {
        dst.sources[stat] = {};
      }
      dst.sources[stat][source] = (dst.sources[stat][source] || 0) + value;
    }
  }
  if (info && info.args === 0) {
    dst[stat] = 1;
  } else if (!info || info.args === 1) {
    if (typeof amount !== "number") {
      amount = amount[0];
    }
    if (dst[stat] != null && (!info || !info.nostack)) {
      if (info && info.dr) {
        dst[stat] = 100 - 0.01 * (100 - dst[stat]) * Math.max(0, 100 - amount * factor);
      } else {
        dst[stat] += amount * factor;
      }
    } else {
      dst[stat] = amount * factor;
    }
  } else if (info.args === -1) {
    dst[stat] = dst[stat] || [];
    dst[stat].push(...amount);
  } else {
    if (dst[stat] == null) {
      dst[stat] = {};
    }
    const argnames = (info.argnames || ["min", "max"]);
    if (info && info.dr) {
      dst[stat][argnames[0]] = 100 - 0.01 * (100 - (dst[stat][argnames[0]] || 0)) * Math.max(0, 100 - (amount[0] != null ? amount[0] : amount[argnames[0]]) * factor);
      for (let i = 1; i < info.args; ++i) {
        dst[stat][argnames[i]] = Math.max(dst[stat][argnames[i]] || 0, (amount[i] != null ? amount[i] : amount[argnames[i]]) * factor);
      }
    } else {
      for (let i = 0; i < info.args; ++i) {
        dst[stat][argnames[i]] = ((!info.nostack && dst[stat][argnames[i]]) || 0) + (amount[i] != null ? amount[i] : amount[argnames[i]]) * factor;
      }
    }
  }
}

class BaseStats {
  info = {
    level: level,
    gems: 0,
    legendaries: 0,
    ancients: 0,

    mainhand: {
      speed: 1,
      wpnphy: {min: 2, max: 3},
    },
    sets: {},
    itemregen: 0,
  };

  sources = {};
  special = {};
  affixes = {};
  gems = {};
  gearstats = {str: 0, dex: 0, int: 0, vit: 0};

  constructor(charClass) {
    this.charClass = charClass;
    this.primary = Data.classes[charClass].primary;
  }

  addAbsolute(stat, from) {
    if (!this[from]) return;
    const info = Data.statInfo[stat];
    const sources = this.sources;
    if (sources[from]) {
      if (!sources[stat]) {
        sources[stat] = {};
      }
      for (const src in sources[from]) {
        if (sources[stat][src] && info && info.dr) {
          sources[stat][src] = 100 - 0.01 * (100 - sources[stat][src]) * (100 - sources[from][src]);
        } else {
          sources[stat][src] = (sources[stat][src] || 0) + sources[from][src];
        }
      }
    }
    if (this[stat] && info && info.dr) {
      this[stat] = 100 - 0.01 * (100 - this[stat]) * (100 - this[from]);
    } else {
      this[stat] = (this[stat] || 0) + this[from];
    }
  }

  addPercent(stat, from, base) {
    let sources, total;
    if (typeof from === "string") {
      if (!this[from]) return;
      sources = this.sources[from];
      total = this[from];
    } else {
      sources = {};
      total = 0;
      for (const id of from) {
        if (this[id]) {
          total += this[id];
          const from_sources = this.sources[id];
          if (from_sources) {
            for (const src in from_sources) {
              sources[src] = (sources[src] || 0) + from_sources[src];
            }
          }
        }
      }
      if (!total) return;
    }
    if (base === undefined) {
      if (!this[stat]) return;
      base = this[stat];
    }
    if (sources) {
      if (!this.sources[stat]) {
        this.sources[stat] = {};
      }
      for (const src in sources) {
        if (typeof base === "number") {
          const value = base * 0.01 * sources[src];
          this.sources[stat][src] = (this.sources[stat][src] || 0) + value;
        } else {
          for (const key in base) {
            const value = base[key] * 0.01 * sources[src];
            this.sources[stat][src] = (this.sources[stat][src] || 0) + value;
            break;
          }
        }
      }
    }
    if (typeof base === "number") {
      this[stat] = (this[stat] || 0) + base * 0.01 * total;
    } else {
      if (!this[stat]) this[stat] = {};
      for (const key in base) {
        this[stat][key] = (this[stat][key] || 0) + base[key] * 0.01 * total;
      }
    }
  }

  add(stat, amount, factor, source) {
    addStat(this, stat, amount, factor, source);
  }
  addList(list, source) {
    if (list) {
      for (const stat in list) {
        this.add(stat, list[stat], 1, source);
      }
    }
  }

  getValue(stat) {
    let data = this;
    for (const part of stat.split(".")) {
      data = data[part];
      if (!data) return 0;
    }
    return data;
  }

  execString(expr) {
    expr = expr.replace(/[a-z_][a-z0-9_]*(?:\.[a-z_][a-z0-9_]*)*/g, stat => {
      const val = this.getValue(stat);
      if (typeof val !== "number") {
        return "(" + JSON.stringify(val) + ")";
      }
      return val;
    });
    // eslint-disable-next-line no-eval
    return eval(expr);
  }

  getSpecial(stat, elem, pet, skill, excl) {
    if (!this.special[stat]) return [];
    const list = [];
    for (const src in this.special[stat]) {
      for (const info of this.special[stat][src]) {
        if (!info.percent) continue;
        if (excl && excl.indexOf(src) >= 0) continue;
        if (info.elems && info.elems.indexOf(elem) < 0) continue;
        if (info.pet !== undefined && info.pet !== (pet || false)) continue;
        if (info.skills && info.skills.indexOf(skill) < 0) continue;
        const name = info.name || Data.sourceNames[src] || src;
        list.push([name, info.percent]);
      }
    }
    return list;
  }

  getTotalSpecial(stat, elem, pet, skill, excl) {
    let total = (Data.statInfo[stat].mult ? 1 : 0);
    if (!this.special[stat]) return total;
    for (const src in this.special[stat]) {
      for (const info of this.special[stat][src]) {
        if (excl && excl.indexOf(src) >= 0) continue;
        if (info.elems && info.elems.indexOf(elem) < 0) continue;
        if (info.pet !== undefined && info.pet !== (pet || false)) continue;
        if (info.skills && info.skills.indexOf(skill) < 0) continue;
        if (Data.statInfo[stat].mult) {
          total *= 1 + 0.01 * info.percent;
        } else {
          total += info.percent;
        }
      }
    }
    return total;
  }

  startLoad(profile) {
    this.setSlots = {};
  }

  addItem(slot, data) {
    if (!data || !Data.itemById[data.id]) return;
    const item = Data.itemById[data.id];
    const itemType = Data.itemTypes[item.type];
    if (slot === "offhand") {
      this.info.ohtype = item.type;
    }
    if (item.quality === "set" || item.quality === "legendary" || item.quality === "ethereal") {
      this.info.legendaries += 1;
    }
    if (data.ancient || item.quality === "ethereal") {
      this.info.ancients += 1;
    }
    if (itemType.weapon) {
      let weapon = itemType.weapon;
      if (item.weapon) weapon = Object.assign({}, weapon, item.weapon);
      this.info[slot] = {
        speed: weapon.speed * (1 + 0.01 * (data.stats.weaponias || [0])[0]),
        ias: (data.stats.weaponias || [0])[0],
        wpnphy: {min: weapon.min, max: weapon.max},
        damage: (data.stats.damage || [0])[0],
        type: item.type,
        slot: itemType.slot,
        weaponClass: weapon.type,
      };
    }
    if (item.set) {
      this.info.sets[item.set] = (this.info.sets[item.set] || 0) + 1;
      if (!this.setSlots[item.set]) {
        this.setSlots[item.set] = slot;
      }
    }
    const _addStat = (stat, value, required) => {
      let statid = stat;
      if (stat === "custom") {
        if (required?.custom.options) {
          const custom = Data.itemById[data.customId || required.custom.options[0]]?.required?.custom;
          if (custom) {
            statid = custom.id;
            Data.statInfo[statid] = custom;
          }
        } else if (required) {
          statid = required.custom.id;
          Data.statInfo[statid] = required.custom;
        }
        if (statid) {
          Data.statInfo[statid].nostack = true;
          if (Data.statInfo[statid].args === undefined) {
            Data.statInfo[statid].args = 1;
          }
        }
      }
      if (!statid) return;
      if (stat === "custom" || Data.stats[stat]?.affix) {
        this.affixes[statid] = {
          slot: slot,
          value,
        };
      }
      if (Data.statInfo[statid].damage && itemType.weapon) {
        if (slot === "mainhand") {
          this.info.mhelement = Data.stats[stat].elem;
        }
        if (value.length === 1) {
          value = [...value, value[0]];
        }
        addStat(this.info[slot], "wpnphy", value);
      } else if ((statid !== "damage" && statid !== "weaponias") || !itemType.weapon) {
        addStat(this, statid, value, 1, slot);
      }
      if (stat === "regen") {
        this.info.itemregen += value[0];
      }
    };
    for (const stat in data.stats) {
      _addStat(stat, data.stats[stat], item.required);
    }
    for (const stat of ['str', 'dex', 'int', 'vit']) {
      if (data.stats[stat]) {
        this.gearstats[stat] += data.stats[stat][0];
      }
    }
    if (data.gems) {
      let factor = 1;
      if (data.stats.custom && item.required.custom.id === "leg_leoricscrown") {
        factor = 1 + 0.01 * data.stats.custom[0];
      }
      if (slot === "head" && this.extraFx?.leg_leoricscrown) {
        factor = 1 + 0.01 * this.extraFx.leg_leoricscrown;
      }
      const slotInfo = (Data.metaSlots[itemType.slot] || Data.itemSlots[itemType.slot]);
      let slotType = slotInfo.socketType;
      if (slotType !== "weapon" && slotType !== "head") {
        slotType = "other";
      }
      this.info.gems += data.gems.length;
      for (const gem of data.gems) {
        if (!gem) continue;
        if (Array.isArray(gem)) {
          const [type, color] = gem;
          if (Data.legendaryGems[type]) {
            this.gems[type] = color;
          } else if (Data.gemColors[color]) {
            const gem = Data.gemColors[color][slotType];
            const value = [gem.amount[type]];
            if (gem.stat === "wpnphy") {
              value.push(value[0]);
            }
            if (Data.statInfo[gem.stat].damage && itemType.weapon) {
              addStat(this.info[slot], "wpnphy", value, factor);
            } else {
              this.add(gem.stat, value, factor, "gems");
              if (this.gearstats[gem.stat] != null) {
                this.gearstats[gem.stat] += value[0];
              }
            }
          }
        } else if (gem.stats) {
          if (itemType.weapon) {
            if (gem.stats.weaponias) this.info[slot].ias += gem.stats.weaponias[0];
            if (gem.stats.damage) this.info[slot].damage += gem.stats.damage[0];
          }
          for (const stat in gem.stats) {
            _addStat(stat, gem.stats[stat], Data.itemById[gem.id]?.required);
          }
        }
      }
    }
    if (itemType.weapon) {
      let weapon = itemType.weapon;
      if (item.weapon) weapon = Object.assign({}, weapon, item.weapon);
      this.info[slot].speed = weapon.speed * (1 + 0.01 * this.info[slot].ias);
    }
  }

  finishLoad(profile) {
    for (const set in this.info.sets) {
      const setInfo = Data.itemSets[set];
      let count = this.info.sets[set];
      if (count >= 2 && this.leg_ringofroyalgrandeur) {
        count += 1;
      }
      for (const [req, bonuses] of Object.entries(setInfo.bonuses)) {
        if (count >= parseInt(req)) {
          const stat = `set_${set}_${req}pc`;
          Data.statInfo[stat] = {args: 0};
          this.add(stat, true);
          for (const bonus of bonuses) {
            if (bonus.stat) {
              this.add(bonus.stat, bonus.value, 1, set);
            }
          }
          this.affixes[stat] = {
            slot: this.setSlots[set],
            set: set,
            pieces: req,
            value: [],
          };
        }
      }
    }
    delete this.setSlots;

    if (this.info.offhand) {
      if (this.info.offhand.weaponClass === "fist") {
        this.info.weaponClass = "dualwield" + (this.info.mainhand.weaponClass === "fist" ? "_ff" : "_sf");        
      } else {
        this.info.weaponClass = "dualwield";
      }
    } else {
      this.info.weaponClass = this.info.mainhand.weaponClass;
    }
  }

  finalize(minimal) {
    //this.chc = Math.min(75, this.chc);
    this.addAbsolute("chc", "extrachc");
    this.chc = Math.min(100, Math.max(0, this.chc));

    if (this.expmul) this.expmul *= 0.1;

    this.addAbsolute("str", "caldesanns_str");
    this.addAbsolute("dex", "caldesanns_dex");
    this.addAbsolute("int", "caldesanns_int");
    this.addAbsolute("vit", "caldesanns_vit");
    this.addAbsolute("str", "allstats");
    this.addAbsolute("dex", "allstats");
    this.addAbsolute("int", "allstats");
    this.addAbsolute("vit", "allstats");
    this.addPercent("str", "str_percent");
    this.addPercent("dex", "dex_percent");
    this.addPercent("int", "int_percent");

    if (!minimal) {
      addStat(this, "armor", this.str, 1, "str");
      addStat(this, "armor", this.dex, 1, "dex");
      addStat(this, "resall", this.int, 0.1, "int");
      this.armor += (this.basearmor || 0);
      this.addPercent("armor", "armor_percent");

      if (this.passives?.holdyourground && this.dodge) {
        this.sources.dodge.holdyourground = -this.dodge;
        this.dodge = 0;
      }

      addStat(this, "blockamount", [0, 0]);
      this.addPercent("blockamount", "blockamount_percent");
      addStat(this, "block", this.baseblock, 1, "baseblock");
      this.block = Math.min(75, this.block || 0);
      this.addAbsolute("block", "extra_block");
      this.addPercent("block", "block_percent");
      this.block = Math.min(100, this.block);

      if (this.leg_justicelantern) {
        this.add("dmgred", this.block * this.leg_justicelantern * 0.01, 1, "P4_Unique_Ring_03");
      }

      let hpPerVit = 10;
      if (this.info.level === 70) {
        hpPerVit = 100;
      } else if (this.info.level > 65) {
        hpPerVit = 5 * this.info.level - 270;
      } else if (this.info.level > 60) {
        hpPerVit = 4 * this.info.level - 205;
      } else if (this.info.level > 35) {
        hpPerVit = this.info.level - 25;
      }
      this.info.hppervit = hpPerVit;
      this.info.hp += this.vit * hpPerVit;
      this.info.hp *= 1 + 0.01 * (this.life || 0);

      var resists = ["resphy", "resfir", "rescol", "reslit", "respsn", "resarc"];
      for (const res of resists) addStat(this, res, this.resall);
      for (const res of resists) this.addPercent(res, ["resist_percent", res + "_percent"]);
      this.info.resavg = 0;
      this.info.resmin = 0;
      for (const res of resists) {
        this.info.resmin += (this[res] < 0 ? -1 : 1) * Math.sqrt(Math.abs(this[res]));
        this.info.resavg += this[res];
      }
      this.info.resavg /= 6;
      this.info.resmin = (this.info.resmin < 0 ? -1 : 1) * (this.info.resmin * this.info.resmin) / 36;
      this.info.defavg = 1 - 0.01 * ((this.meleedef || 0) + (this.rangedef || 0)) * 0.333333;
      const nonphys = (this.nonphys || 0) * 0.01;
      this.info.defavg *= 1 - 0.01 * (this.physdef || 0) * 0.16663;
      this.info.defavg *= 1 - (1 - (1 - nonphys) * (1 - 0.01 * (this.colddef || 0))) * 0.16663;
      this.info.defavg *= 1 - nonphys * 0.16663;
      this.info.defavg *= 1 - nonphys * 0.16663;
      this.info.defavg *= 1 - nonphys * 0.16663;
      this.info.defavg *= 1 - (1 - (1 - nonphys) * (1 + 0.01 * (this.firetaken || 0))) * 0.16663;
      this.info.defavg *= 1 - 0.01 * (this.dmgred || 0);
      this.info.armor_factor = 1 / (1 + this.armor / (this.info.level * 50));
      this.info.res_factor = 1 / (1 + this.info.resavg / (this.info.level * 5));
      this.info.resmin_factor = 1 / (1 + this.info.resmin / (this.info.level * 5));
      this.info.defense_factor = this.info.armor_factor * this.info.res_factor * (1 - 0.01 * (this.edef || 0) / 2) * this.info.defavg;
      this.info.defensemin_factor = this.info.armor_factor * this.info.resmin_factor * (1 - 0.01 * (this.edef || 0) / 2) * this.info.defavg;
      this.info.toughness = this.info.hp / this.info.defense_factor / (1 - 0.01 * (this.dodge || 0));
      this.info.toughnessmin = this.info.hp / this.info.defensemin_factor / (1 - 0.01 * (this.dodge || 0));

      this.info.toughpervit = this.info.toughness / this.info.hp * 100 * hpPerVit * (1 + 0.01 * (this.life || 0));
      this.info.toughperlife = this.info.toughness * 0.01 / (1 + 0.01 * (this.life || 0));
      this.info.toughperarmor = this.info.toughness * 100 / (this.info.level * 50) * this.info.armor_factor * (1 + 0.01 * (this.armor_percent || 0));
      const resMult = 1 + 0.01 * (this.resist_percent || 0) + 0.01 * (
        (this.resphy_percent || 0) + (this.resfir_percent || 0) + (this.rescol_percent || 0) +
        (this.reslit_percent || 0) + (this.respsn_percent || 0) + (this.resarc_percent || 0)) / 6;
      this.info.toughperres = this.info.toughness * 100 / (this.info.level * 5) * this.info.res_factor * resMult;

      this.info.toughper650vit = 100 * this.info.toughpervit * 6.50 / this.info.toughness;
      this.info.toughper15life = 100 * this.info.toughperlife * 15 / this.info.toughness;
      this.info.toughper775armor = 100 * this.info.toughperarmor * 7.75 / this.info.toughness;
      this.info.toughper130res = 100 * this.info.toughperres * 1.30 / this.info.toughness;

      this.info.resphy_factor = 1 / (1 + this.resphy / (this.info.level * 5));
      this.info.resfir_factor = 1 / (1 + this.resfir / (this.info.level * 5));
      this.info.rescol_factor = 1 / (1 + this.rescol / (this.info.level * 5));
      this.info.reslit_factor = 1 / (1 + this.reslit / (this.info.level * 5));
      this.info.respsn_factor = 1 / (1 + this.respsn / (this.info.level * 5));
      this.info.resarc_factor = 1 / (1 + this.resarc / (this.info.level * 5));
    }

    this.addAbsolute("thorns", "firethorns");
    this.addPercent("thorns", "vit_to_thorns", this.vit);
    this.addPercent("thorns", "thorns_multiply");
    this.addPercent("thorns", "thorns_percent");
    this.info.thorns = (this.thorns || 0) * (1 + this[this.primary] / 100);

    const calcWeapon = info => {
      info.speed += (this.weaponaps || 0);
      info.speed *= 1 + 0.01 * (this.weaponaps_percent || 0);
      let factor = (1 + 0.01 * (info.damage || 0));
      info.wpnphy.min *= factor;
      info.wpnphy.max *= factor;
      if (this.wpnphy) {
        info.wpnphy.min += this.wpnphy.min;
        info.wpnphy.max += this.wpnphy.max;
      }
      info.wpnbase = Object.assign({}, info.wpnphy);
      factor = (1 + 0.01 * this[this.primary]) * (1 + 0.01 * (this.damage || 0));
      factor *= 1 + 0.01 * (this[`damage_${this.charClass}`] || 0);
      info.wpnphy.min *= factor;
      info.wpnphy.max *= factor;
      info.damage = (info.wpnphy.min + info.wpnphy.max) * 0.5;
      info.speed *= 1 + 0.01 * this.ias;
      info.speed = Math.min(5, info.speed);
      info.dps = info.damage * this.info.critfactor * info.speed;
      info.dph = info.damage * this.info.critfactor;
    };

    if (this.info.offhand) {
      this.add("ias", 15, 1, "dualwield");
    }
    this.info.critfactor = 1 + (0.01 * this.chc) * (0.01 * this.chd);
    calcWeapon(this.info.mainhand);
    if (this.info.offhand) {
      calcWeapon(this.info.offhand);
      this.info.aps = 2 / (1 / this.info.mainhand.speed + 1 / this.info.offhand.speed);
      this.info.dps = (this.info.mainhand.dph + this.info.offhand.dph) * this.info.aps / 2;
    } else {
      this.info.dps = this.info.mainhand.dps;
      this.info.aps = this.info.mainhand.speed;
    }

    this.info.maxelem = "arc";
    this.info.elemental = (this.dmgarc || 0);
    for (const e in Data.elements) {
      const cur = (this["dmg" + e] || 0);
      if (cur > this.info.elemental) {
        this.info.maxelem = e;
        this.info.elemental = cur;
      }
    }

    if (!minimal) {
      this.addAbsolute("regen", "post_regen");
      this.addPercent("regen", "post_regen_bonus", this.info.itemregen);
      this.addPercent("regen", "regen_bonus");
      this.addPercent("regen", "regen_percent", this.info.hp);
      this.info.healing = (this.regen || 0) + (this.lph || 0) * this.info.aps + (this.laek || 0) * 0.16 + (this.healbonus || 0) * 0.08;
      this.info.recovery = this.info.healing * this.info.toughness / this.info.hp;
      this.info.recoverymin = this.info.healing * this.info.toughnessmin / this.info.hp;
    }

    this.info.edps = this.info.dps * (1 + 0.01 * (this.edmg || 0));
    this.info.elementaldps = this.info.dps * (1 + 0.01 * this.info.elemental);

    this.info.dph = this.info.dps / this.info.aps;

    this.info.primary = this[this.primary];

    this.info.dmgmul = 100 * ((this.dmgmul || 1) - 1);
    this.final = {};
    this.final.addbase = 1 + 0.01 * (this.damage || 0) + 0.01 * (this.dmgtaken || 0);
    this.final.chc = this.chc + (this.chctaken || 0);
    this.final.chc *= 1 + 0.01 * (this.chctaken_percent || 0);
    this.final.chc = Math.min(100, Math.max(0, this.final.chc));
    let mult = (this.dmgmul || 1) * this.final.addbase / (1 + 0.01 * (this.damage || 0));
    mult *= (10000 + this.final.chc * this.chd) / (10000 + this.chc * this.chd);
    this.final.dps = this.info.dps * mult;
    this.final.dph = this.info.dph * mult;
    this.final.elemdps = this.final.dps * (1 + 0.01 * this.info.elemental);
    this.final.elemdph = this.final.dph * (1 + 0.01 * this.info.elemental);
    this.final.elemedps = this.final.elemdps * (1 + 0.01 * (this.edmg || 0));
    this.final.elemedph = this.final.elemdph * (1 + 0.01 * (this.edmg || 0));

    if (!minimal) {
      this.ms = Math.min(25, this.ms || 0);
      this.addAbsolute("ms", "extrams");
      this.mf = Math.min(300, this.mf || 0);
      this.info["dpsper" + this.primary] = this.info.elementaldps / (1 + 0.01 * this[this.primary]);
      this.info.dpsperchc = this.info.elementaldps * 0.0001 * this.chd / this.info.critfactor;
      this.info.dpsperchd = this.info.elementaldps * 0.0001 * this.final.chc / this.info.critfactor;
      this.info.dpsperias = this.info.elementaldps * 0.01 / (1 + 0.01 * this.ias);
      this.info.dpsperelem = this.info.elementaldps * 0.01 / (1 + 0.01 * this.info.elemental);

      this.info["dpsper650" + this.primary] = 100 * this.info["dpsper" + this.primary] * 6.50 / this.info.elementaldps;
      this.info.dpsper6chc = 100 * this.info.dpsperchc * 6 / this.info.elementaldps;
      this.info.dpsper50chd = 100 * this.info.dpsperchd * 50 / this.info.elementaldps;
      this.info.dpsper7ias = 100 * this.info.dpsperias * 7 / this.info.elementaldps;
      this.info.dpsper20elem = 100 * this.info.dpsperelem * 20 / this.info.elementaldps;

      const mhbase = (this.info.mainhand.wpnbase.min + this.info.mainhand.wpnbase.max) * 0.5;
      if (this.info.offhand) {
        const mhdph = this.info.mainhand.dph;
        const ohdph = this.info.offhand.dph;
        const ohbase = (this.info.offhand.wpnbase.min + this.info.offhand.wpnbase.max) * 0.5;
        this.info.dpsperdmg = this.info.elementaldps * (mhdph / mhbase + ohdph / ohbase) / (mhdph + ohdph);
      } else {
        this.info.dpsperdmg = this.info.elementaldps / mhbase;
      }

      this.info.dpsper105210dmg = 100 * this.info.dpsperdmg * (105 + 210) / 2 / this.info.elementaldps;
    }
  }
}

export class FollowerStats extends BaseStats {
  chc = 0;
  chd = 50;
  ias = 0;

  factors = {
    str: 2.5,
    dex: 2.5,
    int: 2.5,
    vit: 2.5,
    caldesanns_str: 2.5,
    caldesanns_dex: 2.5,
    caldesanns_int: 2.5,
    caldesanns_vit: 2.5,
  };

  skills = {};

  constructor(charClass, ownerClass) {
    super(charClass);
    this.ownerClass = ownerClass;

    this.regen = 6830.66;
    switch (charClass) {
    case "templar":
      this.str = 217;
      this.dex = 78;
      this.int = 80;
      this.vit = 149;
      this.info.hp = 85383;
      break;
    case "enchantress":
      this.str = 79;
      this.dex = 79;
      this.int = 215;
      this.vit = 147;
      this.info.hp = 68307;
      this.wpnphy = {min: 0, max: 0.1};
      break;
    case "scoundrel":
      this.str = 79;
      this.dex = 218;
      this.int = 78;
      this.vit = 147;
      this.info.hp = 68307;
      break;
    // no default
    }
  }

  getAffixSource(id, profile) {
    if (!profile) {
      profile = Store.getProfile();
    }
    const affix = this.affixes[id];
    if (!affix) return;
    if (affix.set) return affix.set;
    if (affix.slot) {
      const item = profile.followerItems[affix.slot];
      return item ? item.id : null;
    }
  }

  loadItems(profile, replace) {
    this.startLoad(profile);
    for (const slot in Data.itemSlots) {
      let item = replace ? replace(slot) : undefined;
      if (item === undefined) {
        item = profile.followerItems?.[slot];
      }
      this.addItem(slot, item);
    }
    this.finishLoad(profile);
  }

  applyGems(state) {
  }

  loadSkills(state) {
    const profile = state.profiles[state.activeProfile];
    if (this.leg_followerallskills) {
      for (const skill of Data.followerSkillList[this.charClass]) {
        this.skills[skill] = true;
      }
    } else {
      for (const skill of profile.followerSkills) {
        if (Data.followerSkills[skill]) {
          this.skills[skill] = true;
        }
      }
    }

    for (const skill in this.skills) {
      const info = Data.followerSkills[skill];
      if (info && info.selectActive(state) !== false) {
        this.addList(getFollowerBonus(skill, this), skill);
      }
    }

    const postList = [];
    for (const [affix, {value}] of Object.entries(this.affixes)) {
      const info = Data.itemaffixes[affix];
      if (!info) continue;
      let func = "buffs";
      if (!info[func] || info.selectActive(state) === false) {
        func = "inactive";
      }
      if (info[func]) {
        if (typeof info[func] == "function") {
          const apply = () => this.addList(info[func](value, this), this.getAffixSource(affix, profile));
          if (info.last) {
            postList.push(apply);
          } else {
            apply();
          }
        } else {
          this.addList(info[func], this.getAffixSource(affix, profile));
        }
      }
    }
    for (const apply of postList) {
      apply();
    }
  }

  finalize(minimal) {
    super.finalize(minimal);

    if (this.mf) this.mf /= 5;
    if (this.gf) this.gf /= 5;
    if (this.expmul) this.expmul /= 5;
  }
}

export class Stats extends BaseStats {
  str = 7 + level;
  dex = 7 + level;
  int = 7 + level;
  vit = 7 + 2 * level;

  chc = 5;
  chd = 50;
  ias = 0;

  gems = {};
  skills = {};
  passives = {};

  constructor(charClass, follower) {
    super(charClass);

    this[this.primary] += 2 * level;
    this.info.hp = 36 + 4 * this.info.level;

    switch (charClass) {
    case "wizard":
      this.maxap = 100;
      this.apregen = 10;
      break;
    case "demonhunter":
      this.maxhatred = 125;
      this.hatredregen = 5;
      this.maxdisc = 30;
      this.discregen = 1;
      break;
    case "witchdoctor":
      this.maxmana = 750;
      this.manaregen = 50;
      break;
    case "barbarian":
      this.maxfury = 100;
      break;
    case "monk":
      this.maxspirit = 250;
      break;
    case "crusader":
      this.maxwrath = 100;
      this.wrathregen = 2.5;
      break;
    case "necromancer":
      this.maxessence = 200;
      break;
      // no default
    }

    if (follower) {
      this.follower = new FollowerStats(follower, charClass);
    }
  }

  getAffixSource(id, profile) {
    if (!profile) {
      profile = Store.getProfile();
    }
    const affix = this.affixes[id];
    if (!affix) return;
    if (affix.set) return affix.set;
    if (affix.slot) {
      let item = (affix.follower ? profile.followerItems[affix.slot] : profile.items[affix.slot]);
      if (Data.itemaffixes[id]?.soulshard && item?.gems?.[0]?.id) {
        return item.gems[0].id;
      }
      return item ? item.id : null;
    }
    if (affix.kanai) return profile.kanai[affix.kanai];
    if (affix.seasonal) return "seasonal";
    if (affix.follower) return "follower";
  }

  startLoad(profile) {
    super.startLoad(profile);
    this.extraFx = cubeGetStats(profile);
    if (profile.seasonal === Seasons.Grandeur) {
      this.leg_ringofroyalgrandeur = 1;
    } else if (profile.seasonal === Seasons.Nightmares) {
      this.info.sets.nightmares = 2;
      this.setSlots.nightmares = "leftfinger";
    } else if (profile.seasonal === Seasons.Pandemonium) {
      this.affixes.seasonal_pandemonium = {seasonal: true};
    }
    if (profile.seasonal === Seasons.Triune || (profile.seasonal && profile.altar?.includes('major1'))) {
      this.affixes.seasonal_triune_love = {seasonal: true};
      this.affixes.seasonal_triune_determination = {seasonal: true};
    }
  }

  finishLoad(profile) {
    for (const id in this.extraFx) {
      this.add(id, this.extraFx[id]);
    }
    delete this.extraFx;

    super.finishLoad(profile);

    const paragon = paragonGetStats(profile);
    this.paragon = profile.paragon.level;
    if (paragon) {
      for (const stat in paragon) {
        this.add(stat, paragon[stat], 1, "paragon");
        if (stat === "regen") {
          this.info.itemregen += paragon[stat][0];
        }
      }
    }
  }

  loadItems(profile, replace) {
    this.startLoad(profile);
    for (const slot in Data.itemSlots) {
      let item = replace ? replace(slot) : undefined;
      if (item === undefined) {
        item = profile.items[slot];
      }
      this.addItem(slot, item);
    }
    this.finishLoad(profile);

    if (this.follower) {
      this.follower.loadItems(profile);

      const fx = this.follower.affixes;
      for (const id in fx) {
        if (Data.statInfo[id].follower) {
          this.affixes[id] = {
            follower: true,
            ...fx[id],
          };
          addStat(this, id, fx[id].value, 1, "follower");
        }
      }
    }
  }

  applyGems(state) {
    function getGemValue(effect, level) {
      let value, delta;
      if (effect.realvalue) {
        value = effect.realvalue.slice();
        delta = effect.realdelta;
      } else if (effect.value) {
        value = effect.value.slice();
        delta = effect.delta;
      }
      if (!value) return [];
      if (delta) {
        for (let i = 0; i < delta.length; ++i) {
          value[i] += delta[i] * level;
        }
      }
      return value;
    }

    for (const [id, level] of Object.entries(this.gems)) {
      const leg = Data.legendaryGems[id];
      if (!leg) continue;
      const active = (leg.selectActive(state) !== false);
      if (active && leg.buffs) {
        const list = leg.buffs(level, this);
        for (const stat in list) {
          this.add(stat, list[stat], /*factor*/1, id);
        }
      } else {
        if (active) {
          let stat_id = leg.effects[0].stat;
          if (!stat_id) {
            stat_id = `gem_${id}`;
            Data.statInfo[stat_id] = leg.effects[0];
          }
          this.add(stat_id, getGemValue(leg.effects[0], level), /*factor*/1, id);
        } else if (leg.inactive) {
          const list = leg.inactive(level, this);
          for (const stat in list) {
            this.add(stat, list[stat], /*factor*/1, id);
          }
        }

        if (level >= 25 && (active || id === "powerful")) {
          let stat_id = leg.effects[1].stat;
          if (!stat_id) {
            stat_id = `gem_${id}_25`;
            Data.statInfo[stat_id] = leg.effects[1];
          }
          this.add(stat_id, getGemValue(leg.effects[1], level), /*factor*/1, id);
        }
      }
    }
  }

  loadSkills(state) {
    if (this.follower) {
      this.follower.loadSkills(state);
    }

    const profile = state.profiles[state.activeProfile];
    for (const skill of profile.skills) {
      if (skill) this.skills[skill[0]] = skill[1];
    }
    for (const passive of profile.passives) {
      if (passive) this.passives[passive] = true;
    }
    if (this.extra_passive) {
      for (const passive of this.extra_passive) {
        if (Data.passives[profile.class][passive]) {
          this.passives[passive] = true;
        }
      }
    }

    if (profile.altar && profile.seasonal) {
      this.altar = {};
      for (const id of profile.altar) {
        if (Data.altar[id]) {
          this.altar[id] = true;
          let value = Data.altar[id].stats;
          if (typeof value === 'function') value = value(this);
          if (value) this.addList(value, 'altar');
        }
      }
    }

    const mantras = ["mantraofsalvation", "mantraofretribution", "mantraofhealing", "mantraofconviction"];
    for (const skill of Object.entries(this.skills)) {
      const info = Data.skills[profile.class][skill[0]];
      if (info && info.selectActive(state)) {
        this.addList(getSkillBonus(profile.class, skill, this), skill[0]);
      }
      if (!mantras.includes(skill[0])) {
        this.addList(getSkillBonus(profile.class, skill, this, "passive"), skill[0]);
        if (this.follower) {
          this.follower.addList(getSkillBonus(profile.class, skill, this, "follower"), skill[0]);
        }
      }
    }

    for (const passive in this.passives) {
      const info = Data.passives[profile.class][passive];
      if (info && info.selectActive(state) !== false) {
        this.addList(getPassiveBonus(profile.class, passive, this), passive);
      }
    }

    if (profile.class === "monk") {
      for (const mantra of mantras) {
        if (!this.skills[mantra] && this.set_inna_4pc) {
          this.skills[mantra] = "x";
        }
        if (this.skills[mantra]) {
          this.addList(getSkillBonus(profile.class, [mantra, this.skills[mantra]], this, "passive"), mantra);
          if (this.follower) {
            this.follower.addList(getSkillBonus(profile.class, [mantra, this.skills[mantra]], this, "follower"), mantra);
          }
        }
      }
    }

    if (this.follower) {
      for (const skill in this.follower.skills) {
        const info = Data.followerSkills[skill];
        if (info && info.selectActive(state) !== false) {
          this.addList(getFollowerBonus(skill, this.follower, "character"), skill);
        }
      }
    }

    const postList = [];
    const kanaiFx = [];
    for (const type in profile.kanai) {
      if (!cubeSlotValid(profile, type)) continue;
      const stat = cubeGetStat(profile.kanai[type]);
      if (!stat) continue;
      this.affixes[stat[0]] = {kanai: type, value: [stat[1]]};
      const info = Data.itemaffixes[stat[0]];
      if (!info) continue;
      let func = "buffs";
      if (!info[func] || info.selectActive(state) === false) {
        func = "inactive";
      }
      if (info[func]) {
        if (typeof info[func] === "function") {
          const apply = () => this.addList(info[func]([stat[1]], this), profile.kanai[type]);
          if (info.last) {
            postList.push(apply);
          } else {
            apply();
          }
        } else {
          this.addList(info[func], profile.kanai[type]);
        }
        kanaiFx.push(stat[0]);
      }
    }

    for (const [affix, {value}] of Object.entries(this.affixes)) {
      if (kanaiFx.includes(affix)) continue;
      const info = Data.itemaffixes[affix];
      if (!info) continue;
      let func = "buffs";
      if (!info[func] || info.selectActive(state) === false) {
        func = "inactive";
      }
      if (info[func]) {
        if (typeof info[func] == "function") {
          const apply = () => this.addList(info[func](value, this), this.getAffixSource(affix, profile));
          if (info.last) {
            postList.push(apply);
          } else {
            apply();
          }
        } else {
          this.addList(info[func], this.getAffixSource(affix, profile));
        }
      }
    }

    // party buffs
    const applyBuffs = list => {
      for (const [id, info] of Object.entries(list)) {
        if (info.selectActive(state) !== false || info.multiple) {
          this.addList(info.buffs(this), id);
        }
      }
    }
    for (const buffs of Data.partybuffs) {
      applyBuffs(buffs.items);
      const cls = buffs.selectClass(state);
      if (cls) applyBuffs(buffs[cls]);
    }
    //applyBuffs(Data.followerbuffs);
    applyBuffs(Data.shrinebuffs);

    for (const apply of postList) {
      apply();
    }
  }

  finalize(minimal) {
    if (this.follower) {
      if (this.dmgtaken) {
        addStat(this.follower, "dmgtaken", this.dmgtaken, 1, "character");
      }
      if (this.chctaken) {
        addStat(this.follower, "chctaken", this.chctaken, 1, "character");
      }
      this.follower.finalize(minimal);
      if (this.follower.mf) {
        addStat(this, "mf", this.follower.mf, 1, "follower");
      }
      if (this.follower.gf) {
        addStat(this, "gf", this.follower.gf, 1, "follower");
      }
      if (this.follower.expmul) {
        addStat(this, "expmul", this.follower.expmul, 1, "follower");
      }
      if (this.follower.dmgtaken && this.follower.dmgtaken > (this.dmgtaken || 0)) {
        addStat(this, "dmgtaken", this.follower.dmgtaken - (this.dmgtaken || 0), 1, "follower");
      }
      if (this.follower.chctaken && this.follower.chctaken > (this.chctaken || 0)) {
        addStat(this, "chctaken", this.follower.chctaken - (this.chctaken || 0), 1, "follower");
      }
    }

    super.finalize(minimal);

    switch (this.charClass) {
    case "wizard":
      this.addPercent("apregen", "resourcegen");
      this.addAbsolute("rcr_ap", "rcr");
      break;
    case "demonhunter":
      this.addPercent("hatredregen", "resourcegen");
      this.addPercent("discregen", "resourcegen");
      this.addAbsolute("rcr_hatred", "rcr");
      this.addAbsolute("rcr_disc", "rcr");
      break;
    case "witchdoctor":
      this.addPercent("maxmana", "maxmana_percent");
      this.addPercent("manaregen", "manaregen_percent", this.maxmana);
      this.addPercent("manaregen", "resourcegen");
      this.addAbsolute("rcr_mana", "rcr");
      break;
    case "barbarian":
      this.addPercent("furyregen", "resourcegen");
      this.addPercent("lifefury", "lpfs_percent");
      this.addAbsolute("rcr_fury", "rcr");
      break;
    case "monk":
      this.addPercent("spiritregen", "resourcegen");
      this.addAbsolute("rcr_spirit", "rcr");
      break;
    case "crusader":
      this.addPercent("wrathregen", "resourcegen");
      this.addAbsolute("rcr_wrath", "rcr");
      break;
    case "necromancer":
      this.addPercent("maxessence", "maxessence_percent");
      this.addPercent("essenceregen", "resourcegen");
      this.addAbsolute("rcr_essence", "rcr");
      break;
    // no default
    }
  }
}

export function computeStats(state, replace, skipBonuses) {
  if (!Data.loaded) {
    throw Error("data is not loaded");
  }

  const profile = state.profiles[state.activeProfile];
  const stats = new Stats(profile.class, profile.follower);
  stats.loadItems(profile, replace);
  if (!skipBonuses) {
    stats.applyGems(state);
    stats.loadSkills(state);
  }
  stats.finalize();
  return stats;
}

let statsCache = null;
let statsCacheProfile = null;
let statsCacheActive = null;
let statsComputing = false;

export function getStats(state) {
  if (statsComputing) {
    throw Error("recursive call to getStats");
  }
  if (!state) {
    state = Store.instance.getState();
  }
  const profile = state.profiles[state.activeProfile];
  const active = state.active;
  if (statsCacheProfile === profile && statsCacheActive === active) {
    return statsCache;
  }
  statsComputing = true;
  // compute twice to get proper params
  statsCache = computeStats(state);
  statsCache = computeStats(state);
  statsCacheProfile = profile;
  statsCacheActive = active;
  statsComputing = false;

  return statsCache;
}

export function getLastStats() {
  if (statsComputing) {
    if (!statsCache) {
      statsCache = computeStats(Store.instance.getState(), () => null, true)
    }
    return statsCache;
  }
  return getStats();
}
